import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import type { Event, EventAttendance } from '../types';
import {
  getEvent,
  getCurrentUser,
  addEventAttendance,
  removeEventAttendance,
} from '../lib/api-client';

const EventContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const EventCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  marginBottom: theme.spacing(3),
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
}));

export const EventDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentUser = getCurrentUser();
  const [isAttending, setIsAttending] = useState(false);
  const [isAttendanceLoading, setIsAttendanceLoading] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Check if user is attending this event
  useEffect(() => {
    if (currentUser && eventId) {
      const attending = currentUser.events?.some((e) => e.eventId === eventId) ?? false;
      setIsAttending(attending);
    }
  }, [currentUser, eventId]);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        setError('No event ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const eventData = await getEvent(eventId);
        setEvent(eventData);
      } catch (err: any) {
        console.error('Failed to fetch event:', err);
        setError(err.message || 'Failed to load event details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  // Handle attend button click
  const handleAttend = async () => {
    if (!currentUser || !event || !eventId) return;

    setIsAttendanceLoading(true);
    try {
      const eventAttendance: EventAttendance = {
        id: `${currentUser.uid}-${eventId}`,
        eventId: eventId,
        eventName: event.name,
        eventDate: event.date,
        competitions: [],
        isPublic: true,
        logistics: {
          isRegistered: false,
          hotelBooked: false,
          transportationArranged: false,
        },
      };
      await addEventAttendance(currentUser.uid, eventAttendance);
      setIsAttending(true);
    } catch (err: any) {
      setSnackbarMessage(err.message || 'Failed to add event');
      setIsSnackbarOpen(true);
    } finally {
      setIsAttendanceLoading(false);
    }
  };

  // Handle not attending button click
  const handleNotAttending = async () => {
    if (!currentUser || !eventId) return;

    setIsAttendanceLoading(true);
    try {
      await removeEventAttendance(currentUser.uid, eventId);
      setIsAttending(false);
    } catch (err: any) {
      setSnackbarMessage(err.message || 'Failed to remove event');
      setIsSnackbarOpen(true);
    } finally {
      setIsAttendanceLoading(false);
    }
  };

  if (isLoading) {
    return (
      <EventContainer maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </EventContainer>
    );
  }

  if (error || !event) {
    return (
      <EventContainer maxWidth="md">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Event not found'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/events')}
        >
          Back to Events
        </Button>
      </EventContainer>
    );
  }

  // Helper to format dates from API (handles string, timestamp, or Date)
  const formatDate = (date: any): string => {
    if (!date) return 'TBD';
    if (date.toDate) return date.toDate().toLocaleDateString(); // Firestore Timestamp
    if (typeof date === 'string') return new Date(date).toLocaleDateString();
    if (date instanceof Date) return date.toLocaleDateString();
    return 'TBD';
  };

  return (
    <EventContainer maxWidth="md">
      {/* Header with Back Button */}
      <HeaderBox>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            {event.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {formatDate(event.date)} • {event.location}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/events')}
          sx={{
            borderColor: '#667eea',
            color: '#667eea',
            '&:hover': {
              background: 'rgba(102, 126, 234, 0.1)',
              borderColor: '#667eea',
            },
          }}
        >
          Back to Events
        </Button>
      </HeaderBox>

      {/* Event Details */}
      <EventCard>
        <Grid container spacing={3}>
          {/* Main Info */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Event Details
              </Typography>
              <Chip
                label={event.isActive ? 'Active' : 'Inactive'}
                sx={{
                  bgcolor: '#e3f2fd',
                  color: '#1976d2',
                  fontWeight: 600,
                }}
              />
            </Box>

            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Description
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {event.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Venue
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {event.venueName}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Location
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  📍 {event.location}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Date
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatDate(event.date)}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Registration Deadline
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatDate(event.registrationDeadline)}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Price
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  {event.registrationPrices && event.registrationPrices.length > 0
                    ? event.registrationPrices
                        .map((price) => `${price.description}: $${price.price}`)
                        .join(', ')
                    : 'See website for pricing'}
                </Typography>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {currentUser ? (
                isAttending ? (
                  <Button
                    variant="contained"
                    onClick={handleNotAttending}
                    disabled={isAttendanceLoading}
                    startIcon={
                      isAttendanceLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CloseIcon />
                      )
                    }
                    sx={{
                      flex: 1,
                      minWidth: 150,
                      background: '#e74c3c',
                      '&:hover': {
                        background: '#c0392b',
                      },
                    }}
                  >
                    {isAttendanceLoading ? 'Updating...' : 'Not Attending'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleAttend}
                    disabled={isAttendanceLoading}
                    startIcon={
                      isAttendanceLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{
                      flex: 1,
                      minWidth: 150,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                      },
                    }}
                  >
                    {isAttendanceLoading ? 'Updating...' : 'Attend'}
                  </Button>
                )
              ) : null}
              <Button
                variant="contained"
                sx={{
                  flex: 1,
                  minWidth: 150,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  },
                }}
              >
                Register Now
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.open(event.website, '_blank')}
                sx={{
                  flex: 1,
                  minWidth: 150,
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': {
                    background: 'rgba(102, 126, 234, 0.1)',
                    borderColor: '#667eea',
                  },
                }}
              >
                Visit Website
              </Button>
            </Box>
          </Grid>
        </Grid>
      </EventCard>

      {/* Error Snackbar */}
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setIsSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </EventContainer>
  );
};
